/**
 * Simplified Chinese (zh-CN) Translations
 * 简体中文翻译
 */

import type { PricingMessages } from './types';

const zhCN: PricingMessages = {
    pageTitle: '产品定价',

    hero: {
        title: '选择适合您的方案',
        subtitle: '灵活的定价，强大的功能',
        billingToggle: {
            monthly: '按月付费',
            annual: '按年付费',
            saveLabel: '节省 {percent}%',
        },
    },

    plans: {
        lite: {
            name: '轻量版',
            description: '每月一次体验所有功能',
            cta: '立即购买',
            priceLabel: '{amount} / 月',
        },
        pro: {
            name: '专业版',
            description: '专业从业者的无限撰写',
            cta: '立即购买',
            priceLabel: '{amount} / 月',
            features: {
                unlimited_drafts: '专业版文档额度',
                priority_support: '优先支持',
            },
        },
        max: {
            name: 'Max',
            description: '高频专利撰写的不限量文档创建',
            cta: '立即购买',
            priceLabel: '{amount} / 月',
            features: {
                unlimited_documents: '文档创建数量不限',
            },
        },
        enterprise: {
            name: '企业版',
            description: '团队和企业的定制解决方案',
            cta: '联系我们',
            priceLabel: '自定义价格',
        },
    },

    features: {
        drafting: {
            title: '专利撰写',
            items: {
                specification_generation: '说明书生成',
                claims_generation: '权利要求书生成',
                abstract_generation: '摘要生成',
                embodiment_expansion: '实施例扩展',
                figure_handling: '附图处理',
                drafts_per_month: '每月撰写数量',
            },
        },
        export: {
            title: '导出与格式',
            items: {
                export_word: 'Word 导出',
                export_pdf: 'PDF 导出',
                multi_jurisdiction: '多司法管辖区格式',
                custom_templates: '自定义模板',
            },
        },
        collaboration: {
            title: '协作',
            items: {
                workspace_share: '分享',
                workspace_comment: '评论',
                workspace_edit: '协作编辑',
                version_history: '版本历史',
            },
        },
        administration: {
            title: '管理',
            items: {
                admin_backend: '管理后台',
                user_management: '用户管理',
                usage_analytics: '使用分析',
            },
        },
        security: {
            title: '安全',
            items: {
                data_encryption: '数据加密',
                sso: '单点登录(SSO)',
                audit_logs: '审计日志',
            },
        },
        support: {
            title: '支持',
            items: {
                support_model: '支持模式',
                training: '培训与入门指导',
            },
        },
        values: {
            unlimited: '无限',
            included: '包含',
            notIncluded: '不包含',
            custom: '自定义',
            allJurisdictions: '所有专利局',
        },
    },

    faq: {
        title: '常见问题',
        items: [
            {
                question: '生成的专利质量如何？',
                answer:
          '我们的AI系统经过大量高质量专利数据训练，能够生成符合专业标准的专利文档。所有生成的内容都经过精心设计，确保逻辑清晰、表述准确。',
            },
            {
                question: '支持哪些专利局格式？',
                answer:
          '我们支持包括中国国家知识产权局(CNIPA)、美国专利商标局(USPTO)、欧洲专利局(EPO)在内的主要专利局格式，以及其他多个国家和地区的专利局格式。',
            },
            {
                question: '数据安全如何保障？',
                answer:
          '我们采用银行级加密技术保护您的数据，所有数据传输均使用SSL加密。我们严格遵守数据保护法规，不会与第三方共享您的专利内容。',
            },
            {
                question: '是否需要专业技术背景？',
                answer:
          '不需要。我们的系统设计简单直观，任何人都可以轻松上手。对于专业用户，我们也提供高级功能和自定义选项。',
            },
            {
                question: '可以定制化吗？',
                answer:
          '可以。企业版提供完整的定制化服务，包括专属模板、工作流程定制、API集成等。我们的团队会根据您的具体需求提供解决方案。',
            },
            {
                question: '价格如何计算？',
                answer:
          '轻量版按每月9.9美元计费，每月可撰写1篇专利。专业版提供月付和年付两种选择，年付可享受20%折扣。企业版价格根据团队规模和需求定制。',
            },
            {
                question: '支持团队协作吗？',
                answer:
          '专业版和企业版都支持团队协作功能，包括文档分享、评论、版本历史等。企业版还提供更高级的协作编辑和用户管理功能。',
            },
            {
                question: '如果不满意可以退款吗？',
                answer:
          '我们提供7天无理由退款保证。如果您对服务不满意，可以在购买后7天内申请全额退款。',
            },
        ],
    },

    cta: {
        title: '准备好开始了吗？',
        subtitle: '选择适合您的方案，立即开始您的专利撰写之旅',
        buttonText: '立即开始',
        selectPlan: '选择方案',
        contactSales: '联系销售',
        learnMore: '了解更多',
        getStarted: '立即开始',
        emailSubject: 'Eureka Drafting - 企业版咨询',
        planSelected: '已选择 {plan} 方案',
        getStartedMessage: '即将跳转到注册页面...',
    },

    errors: {
        loadPlansFailed: '加载定价信息失败，请稍后重试',
        invalidPlan: '无效的定价计划',
        networkError: '网络连接失败',
    },

    common: {
        perMonth: '/ 月',
        perYear: '/ 年',
        billedMonthly: '按月计费',
        billedAnnually: '按年计费',
        popular: '热门',
        recommended: '推荐',
        comingSoon: '即将推出',
    },

    payment: {
        verifying: '正在验证支付...',
        pleaseWait: '请稍候，我们正在确认您的支付',
        backToPricing: '返回定价页',
        tryAgain: '重试',

        success: {
            title: '支付成功！',
            message: '感谢您的订阅，您的账户已激活。',
        },

        failed: {
            title: '支付失败',
            message: '我们无法处理您的支付，请重试。',
        },

        canceled: {
            title: '支付已取消',
            message: '您取消了支付，没有产生任何费用。',
        },

        errors: {
            createSessionFailed: '创建支付会话失败，请重试。',
            verifyFailed: '验证支付状态失败',
            verificationFailed: '支付验证失败',
            verificationError: '验证过程中发生错误',
            invalidCallback: '无效的支付回调',
            noPriceId: '价格信息未配置',
        },
    },
};

export default zhCN;
